import React, { useEffect, useRef, useState } from 'react';
import { useLayout, LAYOUTS } from '../context/LayoutContext';
import styles from '../styles/layoutSelector.module.css';

const LAYOUT_DEFS = [
  {
    id: LAYOUTS.SINGLE,
    name: 'Single',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="38" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 10h24M8 15h18M8 20h14" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
      </svg>
    ),
  },
  {
    id: LAYOUTS.SPLIT_H,
    name: 'Side by Side',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="17" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <rect x="22" y="1" width="17" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: LAYOUTS.SPLIT_V,
    name: 'Stacked',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="38" height="12" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <rect x="1" y="17" width="38" height="12" rx="3" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: LAYOUTS.TRIPLE_L,
    name: 'Focus Left',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="21" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.08" />
        <rect x="26" y="1" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="26" y="17" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: LAYOUTS.TRIPLE_R,
    name: 'Focus Right',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="1" y="17" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="18" y="1" width="21" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.08" />
      </svg>
    ),
  },
  {
    id: LAYOUTS.TRIPLE_T,
    name: 'Focus Top',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="38" height="15" rx="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.08" />
        <rect x="1" y="20" width="17" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="22" y="20" width="17" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: LAYOUTS.TRIPLE_B,
    name: 'Focus Bottom',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="17" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="22" y="1" width="17" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="1" y="14" width="38" height="15" rx="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.08" />
      </svg>
    ),
  },
  {
    id: LAYOUTS.QUAD,
    name: 'Quad',
    icon: (
      <svg viewBox="0 0 40 30" fill="none">
        <rect x="1" y="1" width="17" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="22" y="1" width="17" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="1" y="17" width="17" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="22" y="17" width="17" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
];

const LayoutSelector = ({ isOpen, onClose, anchorEl }) => {
  const { layout, changeLayout } = useLayout();
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ bottom: 0, left: 0 });

  useEffect(() => {
    if (!isOpen || !anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setPos({
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left + rect.width / 2,
    });
  }, [isOpen, anchorEl]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          anchorEl && !anchorEl.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen, onClose, anchorEl]);

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ bottom: pos.bottom, left: pos.left }}
    >
      <div className={styles.popoverArrow} />
      <div className={styles.grid}>
        {LAYOUT_DEFS.map((l) => (
          <button
            key={l.id}
            className={`${styles.option} ${layout === l.id ? styles.active : ''}`}
            onClick={() => { changeLayout(l.id); onClose(); }}
            title={l.name}
          >
            <div className={styles.preview}>{l.icon}</div>
            <span className={styles.label}>{l.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default LayoutSelector;
